import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useSnapshot } from 'valtio';
import AppSpinner from '../../common/components/AppSpinner';
import {
  handleChooseAll,
  initRepoSelectionStep,
  OnboardingAppSelection,
  onContinueClick,
  resetRepoSelectionStore,
  searchRepos,
  setMonitorAllNewlyCreatedResources,
  useRepoSelection,
} from '../../onboarding/api/onboarding-api';
import {
  closeConnectorConfigureModal,
  closeRepoSelectionModal,
  setIsRepoModalFirstTimeOpen,
} from '../actions/connector-configure-actions';
import ConnectorConfigureStore from '../stores/connector-configure-store';
import ModalDialog from '../../common/components/ModalDialog';
import { openSnackbar } from '../../snackbar/actions/snackbar-actions';
import { Nullable } from 'common-types';
import ModalConfirmationDialog from '../../common/components/ModalConfirmationDialog';
import { useNavigate } from 'react-router-dom';
import { AppPages } from '../../common/routing/app-navigator';
import { isSelectionChanged } from '../utils/repo-selection-utils';
import { setIsLoading } from '../../onboarding/store-actions/repo-selection-store-actions';
import { openExceedScanLimitModal } from '../../new-scan/store/scan-store-actions';
import { scanAll, useScan } from '../../new-scan/actions/scan-actions';

const onChooseAll = (e: React.ChangeEvent<HTMLInputElement>) => {
  handleChooseAll(e.target.checked);
};

const handleSearchRepos = (e: React.ChangeEvent<HTMLInputElement>) => {
  searchRepos(e.target.value);
};

const handleMonitorNewlyCreatedRepos = (
  e: React.ChangeEvent<HTMLInputElement>,
) => {
  setMonitorAllNewlyCreatedResources(e.target.checked);
};

const ConnectorRepoSelectionContainer: FC<
  React.PropsWithChildren<unknown>
> = () => {
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
  const [isStartNewScanModalOpen, setIsStartNewScanModalOpen] = useState(false);
  const [isRepoLimitModalOpen, setIsRepoLimitModalOpen] = useState(false);

  const {
    selectedConnector,
    repoSelectionModalOpen,
    isRepoSelectionModalFirstOpen,
    isSourceControlFamilyConnector,
  } = useSnapshot(ConnectorConfigureStore);

  const {
    filteredRepoTreeData,
    searchValue,
    isChooseAllSelected,
    controlObject,

    isLoading,
    monitorAllNewlyCreatedResources,

    checkedReposUtils,
  } = useRepoSelection();

  const { isContinueDisabled, checkedReposNumber, reposFound } =
    checkedReposUtils;
  const { maxReposToScan, isExceedScanLimit } = useScan();
  let checkboxInitialStateSnapshot =
    useRef<Nullable<typeof controlObject>>(null);

  useEffect(() => {
    if (
      selectedConnector &&
      isSourceControlFamilyConnector &&
      repoSelectionModalOpen
    ) {
      initRepoSelectionStep(
        selectedConnector.id,
        true,
        !isRepoSelectionModalFirstOpen,
      );
    }
    return () => {
      setIsRepoModalFirstTimeOpen(false);
    };
  }, [
    selectedConnector,
    isRepoSelectionModalFirstOpen,
    repoSelectionModalOpen,
    isSourceControlFamilyConnector,
  ]);

  // useEffect(() => {
  //   if (controlObject && !checkboxInitialStateSnapshot.current) {
  //     checkboxInitialStateSnapshot.current = { ...controlObject };
  //   }
  // }, [controlObject]);

  const handleContinue = useCallback(
    async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      setIsLoading(true);
      if (!selectedConnector) {
        openSnackbar('Oops! Could not complete operation, please try again.', {
          variant: 'error',
        });
        closeRepoSelectionModal();
        return;
      }
      const response = await onContinueClick(e, selectedConnector.id, true);
      if (!response) {
        openSnackbar('Oops! Could not complete operation, please try again.');
        return;
      }
      openSnackbar('Saved successfully', { variant: 'success' });

      setIsStartNewScanModalOpen(true);
      closeConnectorConfigureModal();

      setIsLoading(false);
      setIsConfirmationOpen(false);
      closeRepoSelectionModal();
      resetRepoSelectionStore();
      checkboxInitialStateSnapshot.current = null;
    },
    [selectedConnector],
  );

  const onClose = useCallback(
    (_?, __?, confirmed?: boolean) => {
      if (!selectedConnector || checkedReposNumber === 0) {
        closeRepoSelectionModal();
        return;
      }
      const selectionChanged = isSelectionChanged(
        checkboxInitialStateSnapshot.current!,
        controlObject!,
      );
      const checkedReposExceedsMaxAllowed =
        maxReposToScan && checkedReposNumber > maxReposToScan;

      if (selectionChanged && !confirmed && !checkedReposExceedsMaxAllowed) {
        setIsConfirmationOpen(true);
        return;
      }

      if (checkedReposExceedsMaxAllowed) {
        setIsRepoLimitModalOpen(true);
      }

      setIsConfirmationOpen(false);
      resetRepoSelectionStore();
      closeRepoSelectionModal();
      checkboxInitialStateSnapshot.current = null;
    },
    [checkedReposNumber, controlObject, maxReposToScan, selectedConnector],
  );

  const confirmModalCloseWithoutChanges = useCallback(() => {
    onClose(null, null, true);
  }, [onClose]);

  const closeScanNowModal = useCallback(() => {
    setIsStartNewScanModalOpen(false);
  }, []);

  const closeRepoLimitModal = useCallback(() => {
    setIsRepoLimitModalOpen(false);
  }, []);

  const navigate = useNavigate();
  const onScanNowPress = useCallback(() => {
    resetRepoSelectionStore();
    if (isExceedScanLimit) {
      openExceedScanLimitModal();
      closeScanNowModal();
      return;
    }
    scanAll();
    navigate(AppPages.Dashboard);
  }, [closeScanNowModal, isExceedScanLimit, navigate]);
  return (
    <>
      <ModalDialog
        open={repoSelectionModalOpen}
        PaperProps={{ style: { borderRadius: '15px', maxWidth: 450 } }}
        modalHeader={`Configure your ${
          selectedConnector ? selectedConnector.displayName : 'Git'
        } connector`}
        onClose={onClose}
        scroll='body'>
        {(!filteredRepoTreeData || !maxReposToScan) && (
          <AppSpinner disablePositionAbsolute text='Fetching your repos' />
        )}
        {filteredRepoTreeData && maxReposToScan && (
          <OnboardingAppSelection
            reposNodes={filteredRepoTreeData}
            reposFound={reposFound || 0}
            chooseAllSelected={isChooseAllSelected}
            onSelectChooseAll={onChooseAll}
            handleSearchRepos={handleSearchRepos}
            onContinueClick={handleContinue}
            searchValue={searchValue}
            isLoading={isLoading}
            disableContinue={isContinueDisabled}
            handleMonitorNewResources={handleMonitorNewlyCreatedRepos}
            monitorNewResources={monitorAllNewlyCreatedResources}
            submitButtonText='Save'
            checkedReposNumber={checkedReposNumber}
            reposAmountAllowed={maxReposToScan}
          />
        )}
        <ModalConfirmationDialog
          onConfirmation={handleContinue}
          onCloseConfirmationModal={confirmModalCloseWithoutChanges}
          title='Changes Not Saved'
          dialogBody='Are you sure you want to close this window without saving your changes?'
          confirmationButtonText='Save Now'
          cancelButtonText='Close without saving'
          isOpen={isConfirmationOpen}
        />
        <ModalConfirmationDialog
          onConfirmation={closeRepoLimitModal}
          onCloseConfirmationModal={closeRepoLimitModal}
          title='Please choose repositories'
          dialogBody={`Please choose up to ${maxReposToScan} repositories and click "Save"`}
          confirmationButtonText='Close'
          cancelButtonText=''
          isOpen={isRepoLimitModalOpen}
        />
      </ModalDialog>
      <ModalConfirmationDialog
        onConfirmation={onScanNowPress}
        onCloseConfirmationModal={closeScanNowModal}
        title='Run a new Scan?'
        dialogBody={
          <>
            Changes were made to the repository selection. <br /> Would you like
            to run a new scan?
          </>
        }
        confirmationButtonText='Scan now'
        cancelButtonText='Close without scanning'
        isOpen={isStartNewScanModalOpen}
      />
    </>
  );
};

export default ConnectorRepoSelectionContainer;
